import React from "react";
import { OnChangeFn, SortingState } from "@tanstack/react-table";
import { TSortingState } from "../types";

export type UseTableSortingReturn = {
    enableSorting: boolean;
    sorting?: SortingState;
    onSortingChange?: OnChangeFn<SortingState>;
};

const useTableSorting = <TColumnName extends string>(
    sortingProp?: TSortingState<TColumnName>,
    onSortingChangeProp?: (sorting: TSortingState<TColumnName>) => void
): UseTableSortingReturn => {
    const enableSorting = React.useMemo(
        () => typeof onSortingChangeProp === "function" && typeof sortingProp !== "undefined",
        [onSortingChangeProp, sortingProp]
    );
    const sorting = React.useMemo<SortingState>(() => {
        const temp: SortingState = [];
        Object.keys(sortingProp ?? {}).forEach(key => {
            temp.push({ id: key, desc: sortingProp![key] === "desc" });
        });
        return temp;
    }, [sortingProp]);

    const onSortingChange = React.useCallback<OnChangeFn<SortingState>>(
        updaterOrValue => {
            const newSorting = typeof updaterOrValue === "function" ? updaterOrValue(sorting) : updaterOrValue;
            const temp = newSorting.reduce<TSortingState<TColumnName>>((acc, curr) => {
                acc[curr.id as TColumnName] = curr.desc ? "desc" : "asc";
                return acc;
            }, {});
            onSortingChangeProp?.(temp);
        },
        [sorting, onSortingChangeProp]
    );

    const value = React.useMemo<UseTableSortingReturn>(
        () => ({
            enableSorting,
            sorting,
            onSortingChange
        }),
        [sortingProp, onSortingChange]
    );

    return value;
};

export default useTableSorting;
